// Optimized entry point - direct render for fastest FCP
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';
import App from './App';
import './index.css';


// Fallback hero stays visible until React renders — it serves as a fast LCP element.
// We hide it after React mounts to avoid a flash of empty content.

// Error fallback component
const ErrorFallback = ({ error }: { error: Error }) => (
  <div style={{ padding: '20px', textAlign: 'center', fontFamily: 'sans-serif' }}>
    <h1 style={{ color: '#dc2626' }}>⚠️ Erreur de chargement</h1>
    <p>L'application n'a pas pu se charger. Veuillez rafraîchir la page.</p>
    <pre style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
      {error.message}
    </pre>
    <button 
      onClick={() => location.reload()} 
      style={{ padding: '10px 20px', marginTop: '20px', cursor: 'pointer' }}
    >
      Rafraîchir
    </button>
  </div>
);

// Get root element
const container = document.getElementById('root');

if (container) {
  const root = createRoot(container);
  
  // Render immediately - no async bootstrap needed
  root.render(
    <StrictMode>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <HelmetProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </HelmetProvider>
      </ErrorBoundary>
    </StrictMode>
  );
  
  // Hide fallback hero after React has rendered
  requestAnimationFrame(() => {
    const fallback = document.getElementById('fallback-hero');
    if (fallback) {
      fallback.classList.add('fade-out');
      setTimeout(() => {
        fallback.style.display = 'none';
        fallback.setAttribute('aria-hidden', 'true');
      }, 350);
    }
  });
  
  // Log performance in dev
  if (import.meta.env.DEV) {
    console.log('⚡ App rendered at:', performance.now().toFixed(0), 'ms');
  }
} else {
  console.error('Root element not found');
}
